import { useRef, useState, useMemo } from "react";
import { collection, addDoc, doc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { db, storage } from "@/lib/firebase";
import { uploadResponsibilityClaimPhoto } from "@/lib/responsibilityClaimPhoto";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { EmptyState } from "@/components/ui/EmptyState";
import { FilterPills } from "@/components/ui/FilterPills";
import { RescheduleModal } from "@/components/features/RescheduleModal";
import { CreateResponsibilityModal } from "@/components/features/CreateResponsibilityModal";
import {
  acceptReschedule,
  declineReschedule,
  nextStatus,
  type ClaimStatus,
} from "@/lib/responsibilities";
import type { Responsibility, ResponsibilityClaim, MemberProfile } from "@/lib/models";
import { allLinkedEntityIds } from "@/lib/responsibility";

type ViewMode = "today" | "week";

const FILTERS = ["My List", "Needs Claiming", "Others", "Done"] as const;
type FilterType = (typeof FILTERS)[number];

const FILTER_KEYS: Record<FilterType, string> = {
  "My List": "responsibilities.filters.myList",
  "Needs Claiming": "responsibilities.filters.needsClaiming",
  "Others": "responsibilities.filters.others",
  "Done": "responsibilities.filters.done",
};

export function ResponsibilitiesPage() {
  const { householdId, household, members, profile, loading: ctxLoading } = useHouseholdContext();
  const isHouseholdOwner =
    !!profile?.uid && household?.household_owner === profile.uid;
  const t = useTranslations();

  const { data: responsibilities, loading: respLoading } = useFirestoreCollection<Responsibility>(
    householdId ? `households/${householdId}/responsibilities` : null
  );

  const { data: allClaims, loading: claimsLoading } = useFirestoreCollection<ResponsibilityClaim>(
    householdId ? `households/${householdId}/responsibilityClaims` : null
  );

  const [viewMode, setViewMode] = useState<ViewMode>("today");
  const [rescheduleTarget, setRescheduleTarget] = useState<ResponsibilityClaim | null>(null);
  const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(new Set());
  const [toast, setToast] = useState<string | null>(null);
  // Wave L.5: "+ New Responsibility" modal state. Closes the audit gap that
  // blocked manual smoke-testing of Wave L — the web app previously had no
  // create UI for responsibilities. Mirrors iOS AddResponsibilityView.swift
  // and Android AddResponsibilitySheet.kt.
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingResponsibility, setEditingResponsibility] = useState<
    (Responsibility & { uid: string }) | null
  >(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  // Wave L.2: photo source picker state. When the user taps Complete we
  // first ask "Add a photo?" — they can either choose a file (which uploads
  // and then completes the claim) or skip (which completes without a photo).
  // Mirrors the iOS confirmationDialog pattern in
  // Views/Responsibilities/ResponsibilitiesView.swift:487-501.
  const [photoPromptClaim, setPhotoPromptClaim] = useState<ResponsibilityClaim | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const loading = ctxLoading || respLoading || claimsLoading;

  // Filter responsibilities by recurrence for Today view
  const dayFilteredResponsibilities = useMemo(() => {
    if (viewMode === "week") return responsibilities;

    const now = new Date();
    const jsDay = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const isWeekday = jsDay >= 1 && jsDay <= 5;

    return responsibilities.filter((resp) => {
      switch (resp.recurrence) {
        case "daily": return true;
        case "weekdays": return isWeekday;
        case "weekly": {
          if (resp.dayOfWeek == null) return true;
          // Android writes string day names; iOS+web write numeric (0=Sun..6=Sat).
          if (typeof resp.dayOfWeek === "string") {
            return resp.dayOfWeek.toLowerCase() === dayNames[jsDay];
          }
          return resp.dayOfWeek === jsDay;
        }
        case "monthly": return false;
        case "none": return true;
        default: return true;
      }
    });
  }, [responsibilities, viewMode]);

  const memberMap = useMemo(() => {
    const map: Record<string, MemberProfile> = {};
    for (const m of members) {
      if (m.uid) map[m.uid] = m;
    }
    return map;
  }, [members]);

  // Match claims to responsibilities by recurrence period (matching iOS currentPeriodClaim logic)
  // daily/weekdays → claim must be today
  // weekly → claim must be in the current week
  // monthly → claim must be in the current month
  // none/default → any claim matches
  const currentPeriodClaim = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(startOfToday.getTime() + 86400000);

    // Start of week (Sunday)
    const dayOfWeek = now.getDay();
    const startOfWeek = new Date(startOfToday.getTime() - dayOfWeek * 86400000);
    const endOfWeek = new Date(startOfWeek.getTime() + 7 * 86400000);

    // Start/end of month
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    return (resp: Responsibility): ResponsibilityClaim | null => {
      const matching = allClaims.filter((c) => c.responsibilityId === resp.uid);
      if (matching.length === 0) return null;

      for (const claim of matching) {
        if (!claim.dueDate) continue;
        const dueMs = typeof claim.dueDate.toDate === "function"
          ? claim.dueDate.toDate().getTime()
          : (claim.dueDate as { seconds?: number }).seconds != null
            ? (claim.dueDate as { seconds: number }).seconds * 1000
            : 0;
        if (dueMs === 0) continue;

        switch (resp.recurrence) {
          case "daily":
          case "weekdays":
            if (dueMs >= startOfToday.getTime() && dueMs < endOfToday.getTime()) return claim;
            break;
          case "weekly":
            if (dueMs >= startOfWeek.getTime() && dueMs < endOfWeek.getTime()) return claim;
            break;
          case "monthly":
            if (dueMs >= startOfMonth.getTime() && dueMs < endOfMonth.getTime()) return claim;
            break;
          default:
            return claim; // "none" — any claim matches
        }
      }
      return null;
    };
  }, [allClaims]);

  async function handleClaim(responsibility: Responsibility) {
    if (!responsibility.uid || !householdId || !profile?.uid) return;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    await addDoc(collection(db, `households/${householdId}/responsibilityClaims`), {
      responsibilityId: responsibility.uid,
      householdId,
      claimedBy: profile.uid,
      status: "claimed",
      dueDate: Timestamp.fromDate(todayStart),
      created_at: Timestamp.now(),
      isCompleted: false,
    });
  }

  // Open the "Add a photo?" prompt instead of completing immediately. The
  // user picks Choose Photo or Skip from the modal, which then calls
  // completeClaimWithOptionalPhoto below.
  function handleComplete(claim: ResponsibilityClaim) {
    if (!claim.uid || !householdId || !profile?.uid) return;
    setPhotoUploadError(null);
    setPhotoPromptClaim(claim);
  }

  // Actually mark a claim completed, optionally uploading a proof photo
  // first. Errors during upload are surfaced via photoUploadError; the
  // status update is skipped if the upload (which the user explicitly
  // initiated) fails, matching the iOS behaviour of not silently dropping
  // a requested photo.
  async function completeClaimWithOptionalPhoto(
    claim: ResponsibilityClaim,
    file: File | null,
  ) {
    if (!claim.uid || !householdId || !profile?.uid) return;
    const claimRef = doc(db, `households/${householdId}/responsibilityClaims`, claim.uid);
    const updates: Record<string, unknown> = {
      status: "completed",
      isCompleted: true,
      completedBy: profile.uid,
      completedAt: Timestamp.now(),
    };

    if (file) {
      try {
        setPhotoUploading(true);
        const url = await uploadResponsibilityClaimPhoto(
          file,
          storage,
          householdId,
          claim.uid,
        );
        updates.photoUrl = url;
      } catch (err) {
        console.error("Failed to upload responsibility claim photo", err);
        setPhotoUploadError(t("responsibilities.photo.uploadFailed"));
        setPhotoUploading(false);
        return; // bail without writing — user can retry
      }
      setPhotoUploading(false);
    }

    await updateDoc(claimRef, updates);
    setPhotoPromptClaim(null);
  }

  function handlePhotoFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ""; // allow re-selecting the same file
    if (!file || !photoPromptClaim) return;
    void completeClaimWithOptionalPhoto(photoPromptClaim, file);
  }

  async function handleStart(claim: ResponsibilityClaim) {
    if (!claim.uid || !householdId) return;
    const target = nextStatus(claim.status as ClaimStatus, "start");
    if (!target) return;
    const ref = doc(db, `households/${householdId}/responsibilityClaims`, claim.uid);
    await updateDoc(ref, { status: target });
  }

  // "Mark Not Done" deletes the claim entirely so the responsibility returns
  // to the unclaimed pool — mirrors iOS markNotDoneAction in
  // ResponsibilitiesView.swift.
  async function handleMarkNotDone(claim: ResponsibilityClaim) {
    if (!claim.uid || !householdId) return;
    await deleteDoc(doc(db, `households/${householdId}/responsibilityClaims`, claim.uid));
  }

  async function handleSkip(responsibility: Responsibility) {
    if (!householdId || !profile?.uid) return;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    await addDoc(collection(db, `households/${householdId}/responsibilityClaims`), {
      responsibilityId: responsibility.uid,
      householdId,
      claimedBy: profile.uid,
      status: "skipped",
      dueDate: Timestamp.fromDate(startOfToday),
      created_at: Timestamp.now(),
    });
  }

  async function handleUnskip(claim: ResponsibilityClaim) {
    if (!householdId || !claim.uid) return;
    await deleteDoc(doc(db, `households/${householdId}/responsibilityClaims`, claim.uid));
  }

  async function handleAcceptReschedule(claim: ResponsibilityClaim) {
    if (!householdId || !claim.uid) return;
    const ref = doc(db, `households/${householdId}/responsibilityClaims`, claim.uid);
    await updateDoc(ref, acceptReschedule(claim));
    showToast(t("responsibilities.rescheduleAccepted"));
  }

  async function handleDeclineReschedule(claim: ResponsibilityClaim) {
    if (!householdId || !claim.uid) return;
    const ref = doc(db, `households/${householdId}/responsibilityClaims`, claim.uid);
    await updateDoc(ref, declineReschedule(claim));
    showToast(t("responsibilities.rescheduleDeclined"));
  }

  function getClaimStatus(responsibility: Responsibility): {
    label: string;
    claim: ResponsibilityClaim | null;
    claimer: MemberProfile | null;
  } {
    const claim = currentPeriodClaim(responsibility);

    if (!claim) {
      return { label: "unclaimed", claim: null, claimer: null };
    }

    const claimer = claim.claimedBy ? memberMap[claim.claimedBy] ?? null : null;
    const isMe = claim.claimedBy === profile?.uid;

    if (claim.status === "skipped") {
      return { label: "skipped", claim, claimer };
    }
    if (claim.status === "completed" || claim.isCompleted) {
      return { label: "completed", claim, claimer };
    }
    if (claim.status === "reschedule_pending") {
      return { label: "reschedule_pending", claim, claimer };
    }
    if (isMe) {
      if (claim.status === "in_progress") {
        return { label: "in_progress_by_me", claim, claimer };
      }
      return { label: "claimed_by_me", claim, claimer };
    }
    if (claim.status === "in_progress") {
      return { label: "in_progress_by_other", claim, claimer };
    }
    return { label: "claimed_by_other", claim, claimer };
  }

  function toggleFilter(filter: FilterType) {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(filter)) {
        next.delete(filter);
      } else {
        next.add(filter);
      }
      return next;
    });
  }

  // Categorize each responsibility into filter buckets
  function getFilterCategory(label: string): FilterType {
    switch (label) {
      case "claimed_by_me":
      case "in_progress_by_me":
      case "reschedule_pending":
        return "My List";
      case "unclaimed":
        return "Needs Claiming";
      case "claimed_by_other":
      case "in_progress_by_other":
        return "Others";
      case "completed":
        return "Done";
      case "skipped":
        return "Done"; // skipped counts as "done" for filter purposes
      default:
        return "Needs Claiming";
    }
  }

  // Compute filter counts
  const filterCounts = useMemo(() => {
    const counts: Record<FilterType, number> = {
      "My List": 0,
      "Needs Claiming": 0,
      "Others": 0,
      "Done": 0,
    };
    for (const resp of dayFilteredResponsibilities) {
      const { label } = getClaimStatus(resp);
      const category = getFilterCategory(label);
      counts[category]++;
    }
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayFilteredResponsibilities, currentPeriodClaim, profile?.uid]);

  // Filter and optionally group responsibilities
  const displayItems = useMemo(() => {
    // In today view, hide skipped items
    let items = dayFilteredResponsibilities.map((resp) => ({
      resp,
      ...getClaimStatus(resp),
    }));

    if (viewMode === "today") {
      items = items.filter((item) => item.label !== "skipped");
    }

    if (activeFilters.size === 0) return items;

    return items.filter((item) => {
      const category = getFilterCategory(item.label);
      return activeFilters.has(category);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayFilteredResponsibilities, currentPeriodClaim, activeFilters, viewMode, profile?.uid]);

  // Group items by filter category when filters are active
  const groupedItems = useMemo(() => {
    if (activeFilters.size === 0) return null;

    const groups: Record<string, typeof displayItems> = {};
    for (const item of displayItems) {
      const category = getFilterCategory(item.label);
      if (!groups[category]) groups[category] = [];
      groups[category].push(item);
    }
    return groups;
  }, [displayItems, activeFilters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  function formatProposedDateTime(ts: ResponsibilityClaim["rescheduleProposedTime"]): string {
    if (!ts) return "";
    const date =
      typeof (ts as { toDate?: () => Date }).toDate === "function"
        ? (ts as { toDate: () => Date }).toDate()
        : (ts as { seconds?: number }).seconds != null
          ? new Date((ts as { seconds: number }).seconds * 1000)
          : null;
    if (!date) return "";
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }

  function renderResponsibilityCard(
    resp: Responsibility,
    label: string,
    claim: ResponsibilityClaim | null,
    claimer: MemberProfile | null
  ) {
    const isSkipped = label === "skipped";
    // Fold legacy `linkedEntityId` + new `linkedEntityIds` into a single
    // deduped list, then resolve each ID to a member profile (skip
    // unknown IDs so we never render a phantom avatar). Mirrors iOS
    // ResponsibilitiesView.swift:349-366.
    const linkedMembers = allLinkedEntityIds(resp)
      .map((id) => memberMap[id])
      .filter((m): m is MemberProfile => Boolean(m));

    return (
      <Card key={resp.uid} padding="p-4" className={`relative ${isSkipped ? "opacity-50" : ""}`}>
        <div className="space-y-3">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {resp.scheduledTime && (
                  <span className="text-xs font-medium text-gray-500 bg-gray-100 dark:bg-white/10 px-2 py-0.5 rounded">
                    {resp.scheduledTime}
                  </span>
                )}
                {resp.actionLabel && (
                  <span className="text-xs font-medium text-[color:var(--accent-color)] bg-[color:var(--accent-color)]/10 px-2 py-0.5 rounded">
                    {resp.actionLabel}
                  </span>
                )}
              </div>
              {resp.uid && (resp.created_by === profile?.uid || isHouseholdOwner) && (
                <button
                  type="button"
                  onClick={() => setEditingResponsibility(resp as Responsibility & { uid: string })}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10 flex items-center justify-center"
                  aria-label={t("responsibilities.actions.edit")}
                  title={t("responsibilities.actions.edit")}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                  </svg>
                </button>
              )}
              <p className="font-medium mt-1">{resp.title}</p>
              {resp.description && (
                <p className="text-sm text-gray-500 mt-0.5">{resp.description}</p>
              )}
              {/* Linked members — inline avatar + nickname per linked entity. */}
              {linkedMembers.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                  {linkedMembers.map((member) => (
                    <div
                      key={member.uid}
                      className="flex items-center gap-1.5"
                    >
                      <Avatar
                        src={member.profile_url}
                        alt={member.nickname ?? member.name ?? ""}
                        size={20}
                      />
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                        {member.nickname ?? member.name ?? ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {label === "reschedule_pending" && claim?.rescheduleProposedTime && (
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                  {t("responsibilities.reschedule.proposedFor", {
                    time: formatProposedDateTime(claim.rescheduleProposedTime),
                  })}
                </p>
              )}
              {label === "reschedule_pending" && claim?.rescheduleNote && (
                <p className="text-xs text-gray-500 italic mt-0.5">
                  &ldquo;{claim.rescheduleNote}&rdquo;
                </p>
              )}
            </div>
          </div>

          {/* Wave L.2: proof-photo thumbnail (if attached). Click opens lightbox. */}
          {claim?.photoUrl && (
            <button
              type="button"
              onClick={() => setLightboxUrl(claim.photoUrl ?? null)}
              className="block rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:opacity-90 transition-opacity"
              aria-label={t("responsibilities.photo.viewFull")}
            >
              <img
                src={claim.photoUrl}
                alt={t("responsibilities.photo.thumbnailAlt")}
                width={40}
                height={40}
                className="w-10 h-10 object-cover"
              />
            </button>
          )}

          {/* Status + actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {claimer && (
                <Avatar
                  src={claimer.profile_url}
                  alt={claimer.nickname ?? claimer.name ?? ""}
                  size={24}
                />
              )}
              <span className="text-xs text-gray-500">
                {label === "unclaimed" && t("responsibilities.status.unclaimed")}
                {label === "claimed_by_me" && t("responsibilities.status.claimedByMe")}
                {label === "in_progress_by_me" && t("responsibilities.status.inProgressByMe")}
                {label === "claimed_by_other" &&
                  t("responsibilities.status.claimedByOther", {
                    name:
                      claimer?.nickname ??
                      claimer?.name ??
                      t("responsibilities.status.someone"),
                  })}
                {label === "in_progress_by_other" &&
                  t("responsibilities.status.inProgressByOther", {
                    name:
                      claimer?.nickname ??
                      claimer?.name ??
                      t("responsibilities.status.someone"),
                  })}
                {label === "completed" && t("responsibilities.status.completed")}
                {label === "reschedule_pending" && t("responsibilities.status.reschedulePending")}
                {label === "skipped" && t("responsibilities.status.skipped")}
              </span>
            </div>

            <div className="flex gap-2">
              {/* Skip button for unclaimed */}
              {label === "unclaimed" && (
                <button
                  onClick={() => handleSkip(resp)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  {t("responsibilities.actions.skipToday")}
                </button>
              )}

              {/* Claim button */}
              {label === "unclaimed" && (
                <button
                  onClick={() => handleClaim(resp)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-[color:var(--accent-color)] transition-opacity"
                >
                  {t("responsibilities.actions.claim")}
                </button>
              )}

              {/* Undo skip button */}
              {label === "skipped" && claim && (
                <button
                  onClick={() => handleUnskip(claim)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-[color:var(--accent-color)] border border-[color:var(--accent-color)] hover:bg-[color:var(--accent-color)]/10 transition-colors"
                >
                  {t("responsibilities.actions.undoSkip")}
                </button>
              )}

              {/* Accept / Decline reschedule (only for the doer of the claim) */}
              {label === "reschedule_pending" && claim && claim.claimedBy === profile?.uid && (
                <>
                  <button
                    onClick={() => handleDeclineReschedule(claim)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                  >
                    {t("responsibilities.actions.decline")}
                  </button>
                  <button
                    onClick={() => handleAcceptReschedule(claim)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-orange-500 transition-opacity"
                  >
                    {t("responsibilities.actions.accept")}
                  </button>
                </>
              )}

              {/* In-flight (claimed or in_progress) buttons — hidden while reschedule is pending */}
              {(label === "claimed_by_me" ||
                label === "claimed_by_other" ||
                label === "in_progress_by_me" ||
                label === "in_progress_by_other") &&
                claim && (
                  <>
                    <button
                      onClick={() => setRescheduleTarget(claim)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    >
                      {t("responsibilities.actions.reschedule")}
                    </button>

                    {/* Start It — only for the doer when not yet started */}
                    {label === "claimed_by_me" && (
                      <button
                        onClick={() => handleStart(claim)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-[color:var(--accent-color)] transition-opacity"
                      >
                        {t("responsibilities.actions.startIt")}
                      </button>
                    )}

                    {/* Mark Not Done — only for the doer while in_progress */}
                    {label === "in_progress_by_me" && (
                      <button
                        onClick={() => handleMarkNotDone(claim)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                      >
                        {t("responsibilities.actions.markNotDone")}
                      </button>
                    )}

                    <button
                      onClick={() => handleComplete(claim)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-green-500 transition-opacity"
                    >
                      {t("responsibilities.actions.complete")}
                    </button>
                  </>
                )}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("responsibilities.title")}</h1>
        {householdId && profile?.uid && (
          <button
            onClick={() => setIsCreateOpen(true)}
            className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {t("responsibilities.create.button")}
          </button>
        )}
      </div>

      {/* Segmented toggle */}
      <div className="flex bg-gray-100 dark:bg-white/10 rounded-lg p-1">
        <button
          onClick={() => setViewMode("today")}
          className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === "today"
              ? "bg-white dark:bg-gray-800 shadow-sm"
              : "text-gray-500"
          }`}
        >
          {t("responsibilities.viewMode.today")}
        </button>
        <button
          onClick={() => setViewMode("week")}
          className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
            viewMode === "week"
              ? "bg-white dark:bg-gray-800 shadow-sm"
              : "text-gray-500"
          }`}
        >
          {t("responsibilities.viewMode.thisWeek")}
        </button>
      </div>

      {/* Filter pills */}
      <FilterPills
        filters={FILTERS}
        activeFilters={activeFilters}
        onToggle={toggleFilter}
        counts={filterCounts}
        getLabel={(f) => t(FILTER_KEYS[f])}
      />

      {/* Responsibilities list */}
      {dayFilteredResponsibilities.length === 0 ? (
        <EmptyState
          icon="📋"
          title={t("responsibilities.empty.title")}
        />
      ) : groupedItems ? (
        // Grouped by filter category
        <div className="space-y-5">
          {Object.entries(groupedItems).map(([category, items]) => (
            <div key={category} className="space-y-3">
              <h2
                className={`text-sm font-semibold ${
                  category === "Needs Claiming" ? "text-red-500" : "text-gray-500"
                }`}
              >
                {t(FILTER_KEYS[category as FilterType])} ({items.length})
              </h2>
              {items.map((item) =>
                renderResponsibilityCard(item.resp, item.label, item.claim, item.claimer)
              )}
            </div>
          ))}
          {displayItems.length === 0 && (
            <EmptyState icon="🔍" title={t("responsibilities.empty.noFilters")} />
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {displayItems.map((item) =>
            renderResponsibilityCard(item.resp, item.label, item.claim, item.claimer)
          )}
        </div>
      )}

      {/* Reschedule modal */}
      {rescheduleTarget && householdId && profile?.uid && (
        <RescheduleModal
          claim={rescheduleTarget}
          householdId={householdId}
          requestedBy={profile.uid}
          onClose={() => setRescheduleTarget(null)}
        />
      )}

      {/* Wave L.5: Create Responsibility modal — opened by the "+ New
          Responsibility" button at the top of the page. */}
      {householdId && profile?.uid && (
        <CreateResponsibilityModal
          isOpen={isCreateOpen || Boolean(editingResponsibility)}
          existing={editingResponsibility}
          onClose={() => {
            setIsCreateOpen(false);
            setEditingResponsibility(null);
          }}
          householdId={householdId}
          createdBy={profile.uid}
          members={members}
          onError={(msg) => showToast(msg)}
        />
      )}

      {/* Wave L.2: hidden file input for the photo picker. `capture="environment"`
          biases mobile browsers toward the rear camera; desktop browsers ignore it. */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handlePhotoFileSelected}
      />

      {/* Wave L.2: "Add a photo?" source picker. Mirrors iOS confirmationDialog. */}
      {photoPromptClaim && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => {
            if (!photoUploading) setPhotoPromptClaim(null);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-3"
          >
            <h2 className="text-lg font-semibold text-center">
              {t("responsibilities.photo.addPrompt")}
            </h2>
            {photoUploadError && (
              <p className="text-sm text-red-500 text-center">{photoUploadError}</p>
            )}
            {photoUploading ? (
              <p className="text-sm text-gray-500 text-center py-4">
                {t("responsibilities.photo.uploading")}
              </p>
            ) : (
              <div className="flex flex-col gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] transition-opacity"
                >
                  {t("responsibilities.photo.choosePhoto")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void completeClaimWithOptionalPhoto(photoPromptClaim, null);
                  }}
                  className="w-full py-2.5 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  {t("responsibilities.photo.skipPhoto")}
                </button>
                <button
                  type="button"
                  onClick={() => setPhotoPromptClaim(null)}
                  className="w-full py-2.5 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  {t("responsibilities.photo.cancel")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wave L.2: full-size photo lightbox triggered by tapping a thumbnail. */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <img
            src={lightboxUrl}
            alt={t("responsibilities.photo.thumbnailAlt")}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
